import React from 'react'
import * as rn from 'react-native'

import * as c from '../../common'
import * as r from '../../react-utils'

import { OrientationLocker } from 'react-native-orientation-locker'

export type CCTVSimProps = c.r<{
  /**
   * `aspectRatio` is fucked in React Native, need to calculate the sizes
   * manually for each pic.
   */
  containerHeight: number
  /**
   * `aspectRatio` is fucked in React Native, need to calculate the sizes
   * manually for each pic.
   */
  containerWidth: number
  customerID: string
}>

export default React.memo<CCTVSimProps>(
  ({ customerID, containerHeight, containerWidth }) => {
    const [{ security_cams_is_6: camsIs6 = 'no' }] = r.useCustomer(customerID)

    const selectAllCustomerMedia = React.useMemo(
      (): ReturnType<typeof c.makeSelectAllCustomerMedia> =>
        c.makeSelectAllCustomerMedia(),
      [],
    )
    const selectAllCustomerMediaArgs = React.useMemo(
      (): c.SelectMediaParams => ({
        customerID,
        mediaKind: 'security_cam0',
      }),
      [customerID],
    )
    const allCustomerMedia = c.useSelector((_) =>
      selectAllCustomerMedia(_, selectAllCustomerMediaArgs),
    )
    const camMedia0 = c.values(allCustomerMedia.security_cam0)[0]
    const camSrc0 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia0 ? c.getMediaItemSrc(camMedia0) : '',
      }),
      [camMedia0],
    )
    const camMedia1 = c.values(allCustomerMedia.security_cam1)[0]
    const camSrc1 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia1 ? c.getMediaItemSrc(camMedia1) : '',
      }),
      [camMedia1],
    )
    const camMedia2 = c.values(allCustomerMedia.security_cam2)[0]
    const camSrc2 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia2 ? c.getMediaItemSrc(camMedia2) : '',
      }),
      [camMedia2],
    )
    const camMedia3 = c.values(allCustomerMedia.security_cam3)[0]
    const camSrc3 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia3 ? c.getMediaItemSrc(camMedia3) : '',
      }),
      [camMedia3],
    )
    const camMedia4 = c.values(allCustomerMedia.security_cam4)[0]
    const camSrc4 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia4 ? c.getMediaItemSrc(camMedia4) : '',
      }),
      [camMedia4],
    )
    const camMedia5 = c.values(allCustomerMedia.security_cam5)[0]
    const camSrc5 = React.useMemo(
      (): rn.ImageSourcePropType => ({
        uri: camMedia5 ? c.getMediaItemSrc(camMedia5) : '',
      }),
      [camMedia5],
    )

    const containerStyle = React.useMemo(
      (): rn.ViewStyle => ({
        backgroundColor: '#000000',
        flexDirection: 'row',
        flexWrap: 'wrap',
        height: containerHeight,
        width: containerWidth,
      }),
      [containerHeight, containerWidth],
    )

    const imgStyle4 = React.useMemo(
      (): rn.ImageStyle => ({
        height: Math.floor(containerHeight * (1 / 2)),
        width: Math.floor(containerWidth * (1 / 2)),
      }),
      [containerHeight, containerWidth],
    )
    const imgStyle6Big = React.useMemo(
      (): rn.ImageStyle => ({
        height: Math.floor(containerHeight * (2 / 3)),
        width: Math.floor(containerWidth * (2 / 3)),
      }),
      [containerHeight, containerWidth],
    )
    const imgStyle6Small = React.useMemo(
      (): rn.ImageStyle => ({
        height: Math.floor(containerHeight * (1 / 3)),
        width: Math.floor(containerWidth * (1 / 3)),
      }),
      [containerHeight, containerWidth],
    )
    const containerStyle6Vertical = React.useMemo(
      (): rn.ViewStyle => ({
        flexDirection: 'column',
        height: Math.floor(containerHeight * (2 / 3)),
        width: Math.floor(containerWidth * (1 / 3)),
      }),
      [containerHeight, containerWidth],
    )

    const baseNode = (() => {
      if (camsIs6 === 'no') {
        return (
          <rn.View style={containerStyle}>
            <rn.Image resizeMode="cover" style={imgStyle4} source={camSrc0} />
            <rn.Image resizeMode="cover" style={imgStyle4} source={camSrc1} />
            <rn.Image resizeMode="cover" style={imgStyle4} source={camSrc2} />
            <rn.Image resizeMode="cover" style={imgStyle4} source={camSrc3} />
          </rn.View>
        )
      }

      if (camsIs6 === 'yes') {
        return (
          <rn.View style={containerStyle}>
            <rn.Image
              resizeMode="cover"
              style={imgStyle6Big}
              source={camSrc0}
            />

            <rn.View style={containerStyle6Vertical}>
              <rn.Image
                resizeMode="cover"
                style={imgStyle6Small}
                source={camSrc1}
              />
              <rn.Image
                resizeMode="cover"
                style={imgStyle6Small}
                source={camSrc2}
              />
            </rn.View>

            <rn.Image
              resizeMode="cover"
              style={imgStyle6Small}
              source={camSrc3}
            />
            <rn.Image
              resizeMode="cover"
              style={imgStyle6Small}
              source={camSrc4}
            />
            <rn.Image
              resizeMode="cover"
              style={imgStyle6Small}
              source={camSrc5}
            />
          </rn.View>
        )
      }

      return <rn.Text>Loading...</rn.Text>
    })()

    return (
      <>
        {baseNode}

        <OrientationLocker orientation="ALL_ORIENTATIONS_BUT_UPSIDE_DOWN" />
      </>
    )
  },
)
